import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { SuiContainer, SuiLabel } from '@splashthat/sui';
import ColorPickerEditor from 'common/components/ColorPicker';
import BorderEditor from 'common/components/BorderPicker';
import InputTypeEditor from 'common/components/InputTypeEditor';
import FontPickerEditor from 'common/components/FontPicker';
import { TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON } from 'common/entities/ticketType/selectors';
import {
    setTicketQuantityBorderWeight,
    setTicketQuantityBorderRadius,
    setTicketQuantityBorderSizeTop,
    setTicketQuantityBorderSizeBottom,
    setTicketQuantityBorderSizeRight,
    setTicketQuantityBorderSizeLeft,
    setTicketQuantityBorderColorDefault,
    setTicketQuantityBorderColorHover,
    setTicketQuantityInputType,
    // Todo this four into actions
    setTicketQuantityInputFont,
    setTicketQuantityInputFontSize,
    setTicketQuantityInputAnswerColor,
    setTicketQuantityInputPlaceholderColor,
    setTicketQuantityInputBackgroundColorDefault,
    setTicketQuantityInputBackgroundColorHover,
} from 'common/entities/form/styleConfiguration/actions';
import {
    DEFAULT_INPUT_BORDER_COLOR,
    DEFAULT_INPUT_BORDER_COLOR_HOVER,
    DEFAULT_INPUT_BORDER_RADIUS,
    DEFAULT_INPUT_BORDER_WIDTH,
    DEFAULT_INPUT_ANSWER_COLOR,
    DEFAULT_INPUT_PLACEHOLDER_COLOR,
    DEFAULT_SMALL_TICKET_FONT_SIZE,
    DEFAULT_FONT,
    DEFAULT_INPUT_BACKGROUND_COLOR,
} from 'common/entities/form/styleConfiguration/defaultValues';
import { getDefaultStyle, styleShapePropTypes } from 'common/entities/form/styleConfiguration/utils';

import { DROPDOWN_INPUT_TYPE, TEXT_INPUT_TYPE } from 'common/entities/form/styleConfiguration/constants';
import { getTicketQuantityStyles } from 'common/entities/form/styleConfiguration/selectors';
import { getColors } from 'common/entities/color/selectors';
import { getFonts } from 'common/entities/font/selectors';
import { getFormId, getOpenTicketStylingType } from '../../../../selectors';
import {
    COLOR_PICKER_DEFAULT,
    INPUT_BACKGROUND,
    COLOR_PICKER_HOVER,
    TICKET_BORDER_PANEL_LABEL,
    TICKET_INPUT_TYPE_PANEL_LABEL,
    ANSWER_COLOR_LABEL,
    PLACEHOLDER_COLOR_LABEL,
    QUANTITY_FONT_LABEL,
    INPUT_WORD_LABEL,
    FORMAT_LABEL,
} from '../../../../constants/formStyleLabels';
import { COLLAPSIBLE_CONTENT, COLLAPSIBLE_SECTION } from '../../../../constants/reusableClassNames';
import { QUANTITY_QA } from '../../../../constants/ticketDataQa';
import { BACKGROUND_LABEL_QA, FONT_LABEL_QA } from '../../../../utils/qaIdConstants';

const FormStyleEditorQuantityPanel = ({
    openTicketType,
    formId,
    defaultColor,
    hoverColor,
    borderWidths,
    borderRadius,
    colorList,
    fontList,
    inputType,
    font,
    fontSize,
    answerColor,
    placeholderColor,
    inputBackgroundColorDefault,
    inputBackgroundColorHover,
    ...props
}) => (
    <SuiContainer className={COLLAPSIBLE_CONTENT} data-qa={`${QUANTITY_QA}--panel`}>
        <SuiContainer
            className={COLLAPSIBLE_SECTION}
            data-qa={`${QUANTITY_QA}--section-${TICKET_INPUT_TYPE_PANEL_LABEL}`}
        >
            <SuiLabel text={FORMAT_LABEL} data-qa={`${QUANTITY_QA}--format-label`} />
            <InputTypeEditor
                qaId={`${QUANTITY_QA}--inputType`}
                inputType={inputType}
                onChangeInputType={(value) => props.setTicketQuantityInputType(formId, value, openTicketType)}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${QUANTITY_QA}--section-${FONT_LABEL_QA}`}>
            <SuiLabel text={QUANTITY_FONT_LABEL} data-qa={`${QUANTITY_QA}--font-label`} />
            <FontPickerEditor
                fontFamilyList={fontList}
                defaultFontFamily={font}
                onChangeFontFamily={(id) => props.setTicketQuantityInputFont(formId, id, openTicketType)}
                defaultFontSize={fontSize}
                onChangeFontSize={(size) => props.setTicketQuantityInputFontSize(formId, size, openTicketType)}
                fontFamilyQa={`${QUANTITY_QA}--fontFamily`}
                fontSizeQa={`${QUANTITY_QA}--fontSize`}
            />
            <ColorPickerEditor
                colorList={colorList}
                firstColor={answerColor}
                firstColorLabel={ANSWER_COLOR_LABEL}
                changeFirstColor={(id) => props.setTicketQuantityInputAnswerColor(formId, id, openTicketType)}
                secondColor={placeholderColor}
                secondColorLabel={PLACEHOLDER_COLOR_LABEL}
                changeSecondColor={(id) => props.setTicketQuantityInputPlaceholderColor(formId, id, openTicketType)}
                qaId={QUANTITY_QA}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${QUANTITY_QA}--section-${BACKGROUND_LABEL_QA}`}>
            <SuiLabel text={INPUT_BACKGROUND} data-qa={`${QUANTITY_QA}--background-label`} />
            <ColorPickerEditor
                colorList={colorList}
                firstColorLabel={COLOR_PICKER_DEFAULT}
                secondColorLabel={COLOR_PICKER_HOVER}
                firstColor={inputBackgroundColorDefault}
                secondColor={inputBackgroundColorHover}
                changeFirstColor={(id) =>
                    props.setTicketQuantityInputBackgroundColorDefault(formId, id, openTicketType)
                }
                changeSecondColor={(id) => props.setTicketQuantityInputBackgroundColorHover(formId, id, openTicketType)}
                qaId={QUANTITY_QA}
            />
        </SuiContainer>
        <SuiContainer className={COLLAPSIBLE_SECTION} data-qa={`${QUANTITY_QA}--section-${TICKET_BORDER_PANEL_LABEL}`}>
            <SuiContainer className={'BorderColorPicker'}>
                <BorderEditor
                    componentLabel={INPUT_WORD_LABEL}
                    borderWidths={borderWidths}
                    borderRadius={borderRadius}
                    borderOptionsQa={`${QUANTITY_QA}--borderOptions`}
                    borderDropdownQa={`${QUANTITY_QA}--borderDropdown`}
                    onBorderWidthsChanged={(width) =>
                        props.setTicketQuantityBorderWeight(formId, width, openTicketType)
                    }
                    onBorderRadiusChanged={(radius) =>
                        props.setTicketQuantityBorderRadius(formId, radius, openTicketType)
                    }
                    onChangeTopBorderWidth={(width) =>
                        props.setTicketQuantityBorderSizeTop(formId, width, openTicketType)
                    }
                    onChangeBottomBorderWidth={(width) =>
                        props.setTicketQuantityBorderSizeBottom(formId, width, openTicketType)
                    }
                    onChangeRightBorderWidth={(width) =>
                        props.setTicketQuantityBorderSizeRight(formId, width, openTicketType)
                    }
                    onChangeLeftBorderWidth={(width) =>
                        props.setTicketQuantityBorderSizeLeft(formId, width, openTicketType)
                    }
                />
                <ColorPickerEditor
                    qaId={QUANTITY_QA}
                    colorList={colorList}
                    firstColor={defaultColor}
                    secondColor={hoverColor}
                    firstColorLabel={COLOR_PICKER_DEFAULT}
                    secondColorLabel={COLOR_PICKER_HOVER}
                    changeFirstColor={(id) => props.setTicketQuantityBorderColorDefault(formId, id, openTicketType)}
                    changeSecondColor={(id) => props.setTicketQuantityBorderColorHover(formId, id, openTicketType)}
                />
            </SuiContainer>
        </SuiContainer>
    </SuiContainer>
);

FormStyleEditorQuantityPanel.propTypes = {
    openTicketType: PropTypes.oneOf([TICKET_TYPE_STANDALONE, TICKET_TYPE_NESTED, TICKET_TYPE_ADD_ON]).isRequired,
    defaultColor: styleShapePropTypes,
    hoverColor: styleShapePropTypes,
    borderRadius: PropTypes.number,
    answerColor: styleShapePropTypes,
    placeholderColor: styleShapePropTypes,
    inputBackgroundColorDefault: styleShapePropTypes,
    inputBackgroundColorHover: styleShapePropTypes,
    font: styleShapePropTypes,
    fontSize: PropTypes.number,
    borderWidths: PropTypes.shape({
        top: PropTypes.number,
        right: PropTypes.number,
        bottom: PropTypes.number,
        left: PropTypes.number,
    }),
    formId: PropTypes.string.isRequired,
    colorList: PropTypes.arrayOf(PropTypes.object).isRequired,
    fontList: PropTypes.arrayOf(PropTypes.object).isRequired,
    inputType: PropTypes.oneOf([DROPDOWN_INPUT_TYPE, TEXT_INPUT_TYPE]),
    setTicketQuantityBorderWeight: PropTypes.func.isRequired,
    setTicketQuantityBorderRadius: PropTypes.func.isRequired,
    setTicketQuantityBorderSizeTop: PropTypes.func.isRequired,
    setTicketQuantityBorderSizeBottom: PropTypes.func.isRequired,
    setTicketQuantityBorderSizeRight: PropTypes.func.isRequired,
    setTicketQuantityBorderSizeLeft: PropTypes.func.isRequired,
    setTicketQuantityBorderColorDefault: PropTypes.func.isRequired,
    setTicketQuantityBorderColorHover: PropTypes.func.isRequired,
    setTicketQuantityInputType: PropTypes.func.isRequired,
    setTicketQuantityInputFont: PropTypes.func.isRequired,
    setTicketQuantityInputFontSize: PropTypes.func.isRequired,
    setTicketQuantityInputAnswerColor: PropTypes.func.isRequired,
    setTicketQuantityInputPlaceholderColor: PropTypes.func.isRequired,
    setTicketQuantityInputBackgroundColorDefault: PropTypes.func.isRequired,
    setTicketQuantityInputBackgroundColorHover: PropTypes.func.isRequired,
};

FormStyleEditorQuantityPanel.defaultProps = {
    defaultColor: getDefaultStyle(DEFAULT_INPUT_BORDER_COLOR),
    hoverColor: getDefaultStyle(DEFAULT_INPUT_BORDER_COLOR_HOVER),
    borderRadius: DEFAULT_INPUT_BORDER_RADIUS,
    borderWidths: {
        top: DEFAULT_INPUT_BORDER_WIDTH,
        right: DEFAULT_INPUT_BORDER_WIDTH,
        bottom: DEFAULT_INPUT_BORDER_WIDTH,
        left: DEFAULT_INPUT_BORDER_WIDTH,
    },
    inputType: DROPDOWN_INPUT_TYPE,
    answerColor: getDefaultStyle(DEFAULT_INPUT_ANSWER_COLOR),
    placeholderColor: getDefaultStyle(DEFAULT_INPUT_PLACEHOLDER_COLOR),
    inputBackgroundColorDefault: getDefaultStyle(DEFAULT_INPUT_BACKGROUND_COLOR),
    inputBackgroundColorHover: getDefaultStyle(DEFAULT_INPUT_BACKGROUND_COLOR),
    font: getDefaultStyle(DEFAULT_FONT),
    fontSize: DEFAULT_SMALL_TICKET_FONT_SIZE,
};

const mapStateToProps = (state) => {
    const formId = getFormId(state);
    const colorList = getColors(state);
    const fontList = getFonts(state);
    const openTicketType = getOpenTicketStylingType(state);

    const { inputType, colors, radius, font, fontSize, fontColors, inputBackgroundColors, ...borderWidths } =
        getTicketQuantityStyles(state, formId, openTicketType);

    const { default: defaultColor, hover: hoverColor } = colors;
    const { default: inputBackgroundColorDefault, hover: inputBackgroundColorHover } = inputBackgroundColors;
    const { answer, placeholder } = fontColors;
    return {
        formId,
        defaultColor,
        hoverColor,
        font,
        fontSize,
        answerColor: answer,
        placeholderColor: placeholder,
        inputBackgroundColorDefault,
        inputBackgroundColorHover,
        borderRadius: radius,
        borderWidths,
        colorList,
        fontList,
        inputType,
        openTicketType,
    };
};

const mapDispatchToProps = {
    setTicketQuantityBorderWeight,
    setTicketQuantityBorderRadius,
    setTicketQuantityBorderSizeTop,
    setTicketQuantityBorderSizeBottom,
    setTicketQuantityBorderSizeRight,
    setTicketQuantityBorderSizeLeft,
    setTicketQuantityBorderColorDefault,
    setTicketQuantityBorderColorHover,
    setTicketQuantityInputType,
    setTicketQuantityInputFont,
    setTicketQuantityInputFontSize,
    setTicketQuantityInputAnswerColor,
    setTicketQuantityInputPlaceholderColor,
    setTicketQuantityInputBackgroundColorDefault,
    setTicketQuantityInputBackgroundColorHover,
};

export default connect(mapStateToProps, mapDispatchToProps)(FormStyleEditorQuantityPanel);
